// 1. Imports React
import { useState } from "react";

// 2. Imports externes
import {
  CircleCheckBig,
  CircleSlash2,
  Eye,
  MoreHorizontal,
  Table as TableIcon,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";

// 3. Imports composants UI
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { PaginationControls } from "./ui/pagination-controls";
import { Skeleton } from "./ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { showErrorToast, showSuccessToast } from "./ui/toast";

// 4. Imports hooks personnalisés
import { useCompanyUsers, useUpdateUser } from "@/hooks/useUser";

// 5. Imports types
import type { UserType } from "@/types/user.type";
import { useNavigate } from "react-router-dom";

// 6. Imports utils
import { getInitials } from "@/lib/utils";

// 6. Props
interface CompanyUsersProps {
  companyId: string;
}

// 7. Composant
export const CompanyUsers = ({ companyId }: CompanyUsersProps) => {
  // ========== A. Hooks React ==========
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const navigate = useNavigate();

  // ========== B. Custom Hooks ==========
  const { data: companyUsersData, isLoading: usersLoading } = useCompanyUsers(
    companyId,
    {
      page: currentPage,
      limit: itemsPerPage,
    }
  );

  const { mutate: updateUserMutation } = useUpdateUser();

  const users = companyUsersData?.users || [];
  const pagination = companyUsersData?.pagination || null;

  // ========== C. Handlers ==========
  const handleToggleUserStatus = async (user: UserType) => {
    updateUserMutation(
      { id: user.id!, data: { isActive: !user.isActive } },
      {
        onSuccess: () => {
          showSuccessToast("Statut modifié avec succès");
        },
        onError: (error: Error) => {
          showErrorToast(
            error.message || "Erreur lors de la modification du statut"
          );
        },
      }
    );
  };

  // ========== D. JSX ==========
  return (
    <div className="space-y-4">
      {/* Tableau des utilisateurs - Desktop */}
      <div className="rounded-lg border hidden lg:block">
        <Table>
          <TableHeader className="bg-muted/50">
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>
                {users[0]?.role === "EMPLOYEE" ? "PIN" : "Email"}
              </TableHead>
              <TableHead>Rôle</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Date création</TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {usersLoading ? (
              <>
                {[1, 2, 3].map((i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-10 w-10 rounded-lg" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-40" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-20 rounded-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="ml-auto h-8 w-8 rounded-md" />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <TableIcon className="h-12 w-12 text-muted-foreground" />
                    <div className="text-center">
                      <h3 className="text-lg font-medium text-muted-foreground">
                        Aucun utilisateur trouvé
                      </h3>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user: UserType) => (
                <TableRow key={user.id} className="hover:bg-primary/10">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="text-xs">
                          {getInitials(user.firstName, user.lastName)}
                        </AvatarFallback>
                      </Avatar>
                      <span
                        className="cursor-pointer font-medium hover:underline"
                        onClick={() => navigate(`/users/${user.id}`)}
                      >
                        {user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user.firstName || user.lastName || "Utilisateur"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {user.role === "EMPLOYEE" ? user.pin : user.email}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {user.role === "ADMIN"
                        ? "Administrateur"
                        : user.role === "EMPLOYEE"
                        ? "Employé"
                        : "Super Utilisateur"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={user.isActive ? "default" : "secondary"}
                      className={
                        user.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }
                    >
                      <div className="flex items-center gap-1">
                        {user.isActive ? (
                          <CircleCheckBig className="h-3 w-3" />
                        ) : (
                          <CircleSlash2 className="h-3 w-3" />
                        )}
                        <span>{user.isActive ? "Actif" : "Inactif"}</span>
                      </div>
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString("fr-FR")
                      : "-"}
                  </TableCell>
                  <TableCell className="text-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {user.role === "EMPLOYEE" && (
                          <DropdownMenuItem
                            onClick={() => navigate(`/users/${user.id}`)}
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            Voir détails
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => handleToggleUserStatus(user)}
                        >
                          {user.isActive ? (
                            <>
                              <ToggleLeft className="mr-2 h-4 w-4" />
                              Désactiver
                            </>
                          ) : (
                            <>
                              <ToggleRight className="mr-2 h-4 w-4" />
                              Activer
                            </>
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Cartes des utilisateurs - Mobile/Tablette */}
      <div className="lg:hidden space-y-4">
        {usersLoading ? (
          <>
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-lg" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                  <Skeleton className="h-4 w-40" />
                  <div className="flex gap-2">
                    <Skeleton className="h-5 w-24 rounded-full" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                  </div>
                </div>
              </Card>
            ))}
          </>
        ) : users.length === 0 ? (
          <Card className="p-8">
            <div className="flex flex-col items-center gap-2 text-center">
              <TableIcon className="h-12 w-12 text-muted-foreground" />
              <h3 className="text-lg font-medium text-muted-foreground">
                Aucun utilisateur trouvé
              </h3>
            </div>
          </Card>
        ) : (
          users.map((user: UserType) => (
            <Card key={user.id} className="p-4 sm:p-6">
              <div className="space-y-3">
                {/* Nom et Avatar */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="text-xs">
                        {getInitials(user.firstName, user.lastName)}
                      </AvatarFallback>
                    </Avatar>
                    <span
                      className="cursor-pointer font-medium hover:underline"
                      onClick={() => navigate(`/users/${user.id}`)}
                    >
                      {user.firstName && user.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : user.firstName || user.lastName || "Utilisateur"}
                    </span>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {user.role === "EMPLOYEE" && (
                        <DropdownMenuItem
                          onClick={() => navigate(`/users/${user.id}`)}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          Voir détails
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => handleToggleUserStatus(user)}
                      >
                        {user.isActive ? (
                          <>
                            <ToggleLeft className="mr-2 h-4 w-4" />
                            Désactiver
                          </>
                        ) : (
                          <>
                            <ToggleRight className="mr-2 h-4 w-4" />
                            Activer
                          </>
                        )}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Informations */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      {user.role === "EMPLOYEE" ? "PIN" : "Email"}
                    </span>
                    <span className="font-medium">
                      {user.role === "EMPLOYEE" ? user.pin : user.email}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Rôle</span>
                    <Badge variant="outline">
                      {user.role === "ADMIN"
                        ? "Administrateur"
                        : user.role === "EMPLOYEE"
                        ? "Employé"
                        : "Super Utilisateur"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Statut</span>
                    <Badge
                      variant={user.isActive ? "default" : "secondary"}
                      className={
                        user.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }
                    >
                      <div className="flex items-center gap-1">
                        {user.isActive ? (
                          <CircleCheckBig className="h-3 w-3" />
                        ) : (
                          <CircleSlash2 className="h-3 w-3" />
                        )}
                        <span>{user.isActive ? "Actif" : "Inactif"}</span>
                      </div>
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Date création</span>
                    <span className="font-medium">
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString("fr-FR")
                        : "-"}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination && (
        <PaginationControls
          page={currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.total}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onPageSizeChange={(newSize) => {
            setItemsPerPage(newSize);
            setCurrentPage(1);
          }}
        />
      )}
    </div>
  );
};
